import { useCallback, useEffect, useState } from 'react';
import { ApiError, COOKIE_MODE, usersApi } from '../api';
import { ActionMenu } from '../components/ActionMenu';
import { useShell } from '../components/AppLayout';
import { Dialog } from '../components/Dialog';
import { Field, FieldGroup, useFieldErrors } from '../components/Field';
import { PageHead } from '../components/PageHead';
import { passwordErrorText } from '../components/PasswordChange';
import { isDemoUserId } from '../demo/users';
import { DEMO } from '../demo';
import { t } from '../i18n';
import { When } from '../components/When';
import { canManageUsers } from '../session';
import {
  USER_ROLES,
  knownRoles,
  passwordProblems,
  userErrorKey,
  userFormProblems,
  userStatus,
  type ConsoleUser,
  type UserForm,
  type UserFormProblem,
  type UserRole,
  type UserStatus,
} from '../users';
// R8b per-rule-set permissions
import { useRuleSets } from '../hooks/useRuleSets';
import {
  RULE_SET_ACCESS_LEVELS,
  effectiveAccess,
  mapFromRole,
  orderedSets,
  roleCeiling,
  setLabel,
  type RuleSetAccess,
  type RuleSetPermissionMap,
  type RuleSetView,
} from '../ruleSets';

/**
 * User Management (R8) — who can sign in to the console and with which roles. Admin only: the
 * navigation entry is hidden from everyone else, and a deep link shows a refusal (the admin
 * service answers 403 to them anyway).
 *
 * Create (with a temporary password the user must change at first sign-in), edit name, email,
 * roles and the disabled switch, reset a password (which also unlocks), and delete. There is
 * no rename — the username is the account's key in the store and in the audit trail — and no
 * "show password": only a hash is stored. The admin service's refusals (the last Admin, your
 * own account, somebody else's concurrent edit) are shown in words.
 *
 * R8b: the user dialog carries a per-rule-set matrix (sets × none / view / edit draft / publish).
 * A user — new or existing — without a map follows the role on every set, sets created later
 * included, until the Admin changes a level (then the map is saved whole, the role's level as the
 * starting point). The role stays the upper limit, and an Admin sees and does everything. Every
 * change is in the audit trail.
 */
export function UsersScreen() {
  const { settings, session, showToast } = useShell();
  const allowed = COOKIE_MODE && canManageUsers(session);
  const [users, setUsers] = useState<ConsoleUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ConsoleUser | null>(null);
  const [resetting, setResetting] = useState<ConsoleUser | null>(null);
  const [deleting, setDeleting] = useState<ConsoleUser | null>(null);

  const me = session?.user.username;

  const reload = useCallback(async () => {
    try {
      const got = await usersApi.list(settings);
      setUsers(Array.isArray(got) ? got : []);
      setError(null);
    } catch (e) {
      setUsers([]);
      setError(errorText(e));
    }
  }, [settings]);

  useEffect(() => {
    if (!allowed) return;
    void reload();
  }, [allowed, reload]);

  if (!allowed) {
    return (
      <>
        <PageHead title={t('users.title')} lead={t('users.lead')} />
        <div className="banner warn" role="status" data-testid="users-forbidden">
          {t('users.forbidden')}
        </div>
      </>
    );
  }

  const writable = (u: ConsoleUser) => !(DEMO && isDemoUserId(u.id));

  return (
    <>
      <PageHead title={t('users.title')} lead={t('users.lead')} />

      {error && (
        <div className="banner error" role="status" data-testid="users-error" style={{ marginBottom: 12 }}>
          {error}
        </div>
      )}

      <section className="card">
        <header className="card-head">
          <h2>{t('users.list.title')}</h2>
          {users && (
            <span className="chip mute" data-testid="users-count">
              {t('users.list.count', { n: users.length })}
            </span>
          )}
          <span className="spacer" />
          <button type="button" className="btn small" data-testid="users-new" onClick={() => setCreating(true)}>
            {t('users.new.action')}
          </button>
        </header>
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 180 }}>{t('users.col.username')}</th>
                <th>{t('users.col.name')}</th>
                <th style={{ width: 220 }}>{t('users.col.roles')}</th>
                <th style={{ width: 170 }}>{t('users.col.status')}</th>
                <th style={{ width: 170 }}>{t('users.col.lastLogin')}</th>
                <th style={{ width: 44 }} aria-label={t('users.col.actions')} />
              </tr>
            </thead>
            <tbody>
              {users === null && (
                <tr>
                  <td colSpan={6} className="empty">
                    {t('users.loading')}
                  </td>
                </tr>
              )}
              {users?.length === 0 && (
                <tr>
                  <td colSpan={6} className="empty" role="status" data-testid="users-empty">
                    {t('users.empty')}
                  </td>
                </tr>
              )}
              {users?.map((u) => {
                const mine = u.id === me;
                const canWrite = writable(u);
                return (
                  <tr
                    key={u.id}
                    data-testid={`user-row-${u.id}`}
                    // UX review P2-10: a row opens its user's edit dialog; the username is the keyboard way in.
                    className={canWrite ? 'clickable' : undefined}
                    onClick={canWrite ? () => setEditing(u) : undefined}
                  >
                    <td className="mono">
                      {canWrite ? (
                        <button
                          type="button"
                          className="rowlink"
                          data-testid={`user-open-${u.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditing(u);
                          }}
                        >
                          {u.username}
                        </button>
                      ) : (
                        u.username
                      )}
                      {mine && (
                        <span className="chip info" style={{ marginLeft: 6 }}>
                          {t('users.you.chip')}
                        </span>
                      )}
                      {!canWrite && (
                        <span className="chip warn" style={{ marginLeft: 6 }}>
                          {t('master.demo.chip')}
                        </span>
                      )}
                    </td>
                    <td>
                      <div>{u.displayName}</div>
                      {u.email && <div className="small muted">{u.email}</div>}
                    </td>
                    <td data-testid={`user-roles-${u.id}`}>
                      <span className="row-wrap" style={{ gap: 4 }}>
                        {knownRoles(u.roles).map((r) => (
                          <span key={r} className={r === 'Admin' ? 'chip accent' : 'chip mute'}>
                            {r}
                          </span>
                        ))}
                      </span>
                    </td>
                    <td>
                      <StatusChip status={userStatus(u)} testid={`user-status-${u.id}`} />
                    </td>
                    <td className="small">{u.lastLoginUtc ? <When iso={u.lastLoginUtc} /> : t('users.lastLogin.never')}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {/* Edit first; the step that removes the account last, set apart and confirmed (UX review P2-10). */}
                      {canWrite && (
                        <ActionMenu
                          variant="row"
                          label={t('users.menu.a11y', { username: u.username })}
                          items={[
                            { label: t('users.menu.edit'), onSelect: () => setEditing(u) },
                            // QA-A-07: your own password is changed with the current one (Change password), never reset.
                            mine
                              ? { label: t('users.menu.reset'), onSelect: () => undefined, disabled: true, title: t('users.menu.reset.self.tip') }
                              : { label: t('users.menu.reset'), onSelect: () => setResetting(u) },
                            ...(mine ? [] : [{ label: t('users.menu.delete'), onSelect: () => setDeleting(u), separated: true }]),
                          ]}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {creating && (
        <UserDialog
          onClose={() => setCreating(false)}
          onSaved={(u) => {
            setCreating(false);
            showToast(t('users.created.toast', { username: u.username }));
            void reload();
          }}
        />
      )}
      {editing && (
        <UserDialog
          initial={editing}
          self={editing.id === me}
          onClose={() => setEditing(null)}
          onSaved={(u) => {
            setEditing(null);
            showToast(t('users.saved.toast', { username: u.username }));
            void reload();
          }}
        />
      )}
      {resetting && (
        <ResetDialog
          user={resetting}
          onClose={() => setResetting(null)}
          onDone={() => {
            showToast(t('users.reset.toast', { username: resetting.username }));
            setResetting(null);
            void reload();
          }}
        />
      )}
      {deleting && (
        <DeleteDialog
          user={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            showToast(t('users.deleted.toast', { username: deleting.username }));
            setDeleting(null);
            void reload();
          }}
        />
      )}
    </>
  );
}

function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    const key = userErrorKey(e.code);
    if (key) return t(key);
  }
  return passwordErrorText(e);
}

const STATUS_CLASS: Record<UserStatus, string> = {
  active: 'chip ok',
  mustChange: 'chip info',
  locked: 'chip warn',
  disabled: 'chip bad',
};

function StatusChip({ status, testid }: { status: UserStatus; testid: string }) {
  return (
    <span className={STATUS_CLASS[status]} data-testid={testid}>
      {t(`users.status.${status}`)}
    </span>
  );
}

// ---- dialogs ---------------------------------------------------------------------------

/** A problem that belongs to no single field — the admin service's refusal. */
function Problem({ text, testid }: { text: string | null; testid: string }) {
  if (!text) return null;
  return (
    <div className="why-missing" role="alert" data-testid={testid} style={{ margin: 0 }}>
      {text}
    </div>
  );
}

const PROBLEM_KEY: Record<UserFormProblem, string> = {
  username: 'users.problem.username',
  displayName: 'users.problem.displayName',
  email: 'users.problem.email',
  roles: 'users.problem.roles',
  short: 'users.problem.short',
  long: 'users.problem.long',
  username_password: 'users.problem.username_password',
  mismatch: 'users.problem.mismatch',
};

/** The field each problem is shown under (UX review P2-5). */
type UserField = 'username' | 'displayName' | 'email' | 'roles' | 'password' | 'confirm';
const FIELD_OF: Record<UserFormProblem, UserField> = {
  username: 'username',
  displayName: 'displayName',
  email: 'email',
  roles: 'roles',
  short: 'password',
  long: 'password',
  username_password: 'password',
  mismatch: 'confirm',
};

/** The first problem of one field, in words — or undefined. */
function errorOf(problems: readonly UserFormProblem[], field: UserField): string | undefined {
  const p = problems.find((x) => FIELD_OF[x] === field);
  return p ? t(PROBLEM_KEY[p]) : undefined;
}

/** The dialog a submit attempt moves focus inside (its first invalid control). */
const dialogRoot = (testid: string) => document.querySelector(`[data-testid="${testid}"]`);

function Foot({ busy, label, testid, onClose, onGo, danger }: {
  busy: boolean;
  label: string;
  testid: string;
  onClose: () => void;
  onGo: () => void;
  danger?: boolean;
}) {
  // Enabled while idle (UX review P2-5): a click with problems shows them under their fields.
  return (
    <>
      <span className="spacer" />
      <button type="button" className="btn" onClick={onClose}>
        {t('btn.cancel.label')}
      </button>
      <button type="button" className={danger ? 'btn danger' : 'btn primary'} data-testid={testid} disabled={busy} onClick={onGo}>
        {label}
      </button>
    </>
  );
}

/** New user (no `initial`) or edit one. The username is fixed once the user exists. */
function UserDialog({ initial, self, onClose, onSaved }: { initial?: ConsoleUser; self?: boolean; onClose: () => void; onSaved: (u: ConsoleUser) => void }) {
  const { settings } = useShell();
  const isNew = initial === undefined;
  const [values, setValues] = useState<UserForm>({
    username: initial?.username ?? '',
    displayName: initial?.displayName ?? '',
    email: initial?.email ?? '',
    roles: initial ? knownRoles(initial.roles) : ['Viewer'],
  });
  const [disabled, setDisabled] = useState(initial?.disabled ?? false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  // R8b per-rule-set permissions: null = no map (the role decides on every set).
  const { list: registry, error: registryError } = useRuleSets(settings);
  const [permissions, setPermissions] = useState<RuleSetPermissionMap | null>(initial?.ruleSetPermissions ?? null);
  const [permissionsTouched, setPermissionsTouched] = useState(false);
  const matrixSets = registry ? orderedSets(registry.sets).filter((s) => !s.archived || (permissions?.[s.id] ?? null) !== null) : [];
  // A field's problem shows once the field was left, or after a click on Create / Save.
  const form = useFieldErrors();

  const problems: UserFormProblem[] = [
    ...userFormProblems(values, isNew),
    ...(isNew ? passwordProblems(password, values.username, confirm) : []),
  ];
  const errorFor = (field: UserField) => form.show(field, errorOf(problems, field));
  const patch = (p: Partial<UserForm>) => setValues((f) => ({ ...f, ...p }));
  const toggleRole = (r: UserRole, on: boolean) => {
    patch({ roles: USER_ROLES.filter((x) => (x === r ? on : values.roles.includes(x))) });
    form.touch('roles');
  };

  const save = async () => {
    if (!form.attempt(problems.length === 0, dialogRoot('user-dialog'))) return;
    setBusy(true);
    setServerError(null);
    try {
      const email = values.email.trim();
      const saved = isNew
        ? await usersApi.create(settings, {
            username: values.username.trim().toLowerCase(),
            displayName: values.displayName.trim(),
            roles: values.roles,
            temporaryPassword: password,
            ...(email ? { email } : {}),
            // R8b: sent only when the Admin changed a level. Untouched, the user has no map and
            // follows the role on every set — including sets created after them (QA 30 Sep).
            ...(permissionsTouched && permissions ? { ruleSetPermissions: permissions } : {}),
          })
        : await usersApi.update(
            settings,
            initial.id,
            {
              displayName: values.displayName.trim(),
              email: email || null,
              roles: values.roles,
              disabled,
              // R8b: sent only when the matrix was changed, so an unrelated edit never pins a user's sets.
              ...(permissionsTouched ? { ruleSetPermissions: permissions } : {}),
            },
            initial.etag,
          );
      setPassword('');
      setConfirm('');
      onSaved(saved);
    } catch (e) {
      setServerError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      busy={busy}
      title={isNew ? t('users.create.title') : t('users.edit.title', { username: initial.username })}
      testid="user-dialog"
      onClose={onClose}
      foot={
        <Foot
          busy={busy}
          label={isNew ? t('users.create.action') : t('users.save.action')}
          testid="user-save"
          onClose={onClose}
          onGo={() => void save()}
        />
      }
    >
      <Field
        label={t('users.form.username')}
        required={isNew}
        hint={isNew ? t('users.form.username.tip') : undefined}
        error={errorFor('username')}
        name="username"
        form={form}
        testid="user-field-username"
      >
        <input
          type="text"
          className="mono-input"
          value={values.username}
          disabled={!isNew}
          spellCheck={false}
          autoComplete="off"
          onChange={(e) => patch({ username: e.target.value.toLowerCase() })}
        />
      </Field>
      <Field label={t('users.form.displayName')} required error={errorFor('displayName')} name="displayName" form={form} testid="user-field-displayName">
        <input type="text" value={values.displayName} onChange={(e) => patch({ displayName: e.target.value })} />
      </Field>
      <Field label={t('users.form.email')} error={errorFor('email')} name="email" form={form} testid="user-field-email">
        <input type="email" value={values.email} onChange={(e) => patch({ email: e.target.value })} />
      </Field>
      <FieldGroup legend={t('users.form.roles')} required error={errorFor('roles')} testid="user-roles">
        {USER_ROLES.map((r) => (
          <label key={r} className="check">
            <input
              type="checkbox"
              checked={values.roles.includes(r)}
              // Your own Admin role cannot be removed here; the admin service refuses it too.
              disabled={Boolean(self) && r === 'Admin' && values.roles.includes('Admin')}
              onChange={(e) => toggleRole(r, e.target.checked)}
            />
            {t(`users.role.${r}`)}
          </label>
        ))}
      </FieldGroup>
      <RuleSetMatrix
        isNew={isNew}
        sets={matrixSets}
        known={registry !== null}
        failed={registry === null && registryError !== null}
        roles={values.roles}
        permissions={permissions}
        onChange={(next) => {
          setPermissions(next);
          setPermissionsTouched(true);
        }}
      />
      {!isNew && (
        <label className="check">
          <input type="checkbox" data-testid="user-disabled" checked={disabled} disabled={Boolean(self)} onChange={(e) => setDisabled(e.target.checked)} />
          {t('users.form.disabled')}
        </label>
      )}
      {isNew && (
        <>
          <Field
            label={t('users.form.tempPassword')}
            required
            hint={t('users.form.tempPassword.tip')}
            error={errorFor('password')}
            name="password"
            form={form}
            testid="user-field-password"
          >
            <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <Field label={t('users.form.confirmPassword')} required error={errorFor('confirm')} name="confirm" form={form} testid="user-field-confirm">
            <input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </Field>
        </>
      )}
      <Problem testid="user-form-error" text={serverError} />
    </Dialog>
  );
}

function ResetDialog({ user, onClose, onDone }: { user: ConsoleUser; onClose: () => void; onDone: () => void }) {
  const { settings } = useShell();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const form = useFieldErrors();
  const problems = passwordProblems(password, user.username, confirm);
  const errorFor = (field: UserField) => form.show(field, errorOf(problems, field));

  const go = async () => {
    if (!form.attempt(problems.length === 0, dialogRoot('user-reset-dialog'))) return;
    setBusy(true);
    setError(null);
    try {
      await usersApi.resetPassword(settings, user.id, password);
      setPassword('');
      setConfirm('');
      onDone();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      busy={busy}
      title={t('users.reset.title', { username: user.username })}
      testid="user-reset-dialog"
      onClose={onClose}
      foot={<Foot busy={busy} label={t('users.reset.action')} testid="user-reset-go" onClose={onClose} onGo={() => void go()} />}
    >
      <div className="small">{t('users.reset.lead', { username: user.username })}</div>
      <Field label={t('users.form.tempPassword')} required error={errorFor('password')} name="password" form={form} testid="user-reset-field-password">
        <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
      </Field>
      <Field label={t('users.form.confirmPassword')} required error={errorFor('confirm')} name="confirm" form={form} testid="user-reset-field-confirm">
        <input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
      </Field>
      <Problem testid="user-reset-error" text={error} />
    </Dialog>
  );
}

function DeleteDialog({ user, onClose, onDeleted }: { user: ConsoleUser; onClose: () => void; onDeleted: () => void }) {
  const { settings } = useShell();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const go = async () => {
    setBusy(true);
    setError(null);
    try {
      await usersApi.remove(settings, user.id, user.etag);
      onDeleted();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      busy={busy}
      title={t('users.delete.title', { username: user.username })}
      testid="user-delete-dialog"
      onClose={onClose}
      foot={<Foot busy={busy} danger label={t('users.delete.action')} testid="user-delete-go" onClose={onClose} onGo={() => void go()} />}
    >
      <div className="small">{t('users.delete.lead', { username: user.username })}</div>
      <Problem testid="user-delete-error" text={error} />
    </Dialog>
  );
}

// ---- R8b per-rule-set permissions -------------------------------------------------------

type Level = RuleSetAccess | 'none';
const LEVELS: readonly Level[] = ['none', ...RULE_SET_ACCESS_LEVELS];
const RANK: Record<Level, number> = { none: 0, view: 1, edit: 2, publish: 3 };

/**
 * Sets × none / view / edit draft / publish, one choice per set (each level includes the ones
 * before it). A level above what the roles allow is disabled — the role is the upper limit — and a
 * stored grant above it says what it is limited to. An Admin needs none of it.
 */
function RuleSetMatrix({
  isNew,
  sets,
  known,
  failed,
  roles,
  permissions,
  onChange,
}: {
  /** A new user: "follow the role" is where the matrix starts, so there is no button back to it. */
  isNew: boolean;
  sets: readonly RuleSetView[];
  known: boolean;
  failed: boolean;
  roles: readonly string[];
  permissions: RuleSetPermissionMap | null;
  onChange: (next: RuleSetPermissionMap | null) => void;
}) {
  const admin = roles.includes('Admin');
  const ceiling = roleCeiling(roles);
  const ids = sets.map((s) => s.id);
  const chosen = (id: string): Level => (permissions === null ? ceiling : (permissions[id] ?? 'none'));
  const choose = (id: string, level: Level) => {
    const next: RuleSetPermissionMap = { ...(permissions ?? mapFromRole(roles, ids)) };
    if (level === 'none') delete next[id];
    else next[id] = level;
    onChange(next);
  };

  return (
    <fieldset className="checkset" data-testid="user-ruleset-matrix">
      <legend>{t('perms.matrix.legend')}</legend>
      <div className="small muted">{t('perms.matrix.lead')}</div>
      {admin && (
        <div className="small" data-testid="perms-admin">
          {t('perms.matrix.admin')}
        </div>
      )}
      {!admin && known && permissions === null && sets.length > 0 && (
        <div className="small" data-testid="perms-following">
          {t('perms.matrix.following')}
        </div>
      )}
      {failed && (
        <div className="small muted" role="status" data-testid="perms-unknown">
          {t('perms.matrix.unknown')}
        </div>
      )}
      {known && sets.length === 0 && <div className="small muted">{t('perms.matrix.empty')}</div>}
      {known && sets.length > 0 && (
        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>{t('perms.matrix.col.set')}</th>
                {LEVELS.map((l) => (
                  <th key={l} style={{ width: 84, textAlign: 'center' }}>
                    {t(`perms.level.${l}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sets.map((s) => {
                const level = admin ? 'publish' : chosen(s.id);
                const effective = admin ? 'publish' : effectiveAccess(roles, permissions, s.id);
                return (
                  <tr key={s.id} data-testid={`perms-row-${s.id}`}>
                    <td>
                      <span className="nowrap" title={s.id}>
                        {setLabel(s)}
                      </span>
                      {s.archived && (
                        <span className="chip mute" style={{ marginLeft: 6 }}>
                          {t('rulesets.archived.chip')}
                        </span>
                      )}
                      {!admin && effective !== level && (
                        <div className="small muted" data-testid={`perms-limited-${s.id}`}>
                          {t('perms.matrix.limited', { level: t(`perms.level.${effective}`) })}
                        </div>
                      )}
                    </td>
                    {LEVELS.map((l) => {
                      const aboveRole = RANK[l] > RANK[ceiling];
                      return (
                        <td key={l} style={{ textAlign: 'center' }}>
                          <input
                            type="radio"
                            name={`perms-${s.id}`}
                            aria-label={t('perms.matrix.cell.a11y', { set: setLabel(s), level: t(`perms.level.${l}`) })}
                            data-testid={`perms-${s.id}-${l}`}
                            checked={level === l}
                            disabled={admin || (aboveRole && level !== l)}
                            title={aboveRole ? t('perms.matrix.ceiling') : undefined}
                            onChange={() => choose(s.id, l)}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!admin && !isNew && permissions !== null && known && (
        <button type="button" className="btn small" data-testid="perms-follow-role" onClick={() => onChange(null)}>
          {t('perms.matrix.follow.action')}
        </button>
      )}
    </fieldset>
  );
}
